import { forwardRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'src/redux-store/appStore';
import { updateFormData } from 'src/redux-store/slices/constructionFormSlice';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';

interface ApplicationDetailsFormProps {
  formData: {
    OwnersName?: string;
    OwnerMobileNumber?: string;
    AgentsLicenseNumber?: string;
    CompanyName?: string;
    ContractorLicenseNumber?: string;
    ArchitectLicenseNumber?: string;
    TenantName?: string;
    TenantEmail?: string;
    TenantPhone?: string;
    NumberOfUnits?: string;
    NumberOfBedrooms?: string;
    BusinessOccupancyClassification?: string;
    ParkingRequirements?: string;
    IndustrialEquipmentSpecifications?: string;
    AgriculturalEquipmentSpecifications?: string;
    LandUseAndCropPlan?: string;
    ResidentialUnitBreakdown?: string;
    CommercialUnitBreakdown?: string;
    SharedFacilityManagementPlan?: string;
    PrivatePropertyUsePlan?: string;
    PropertyTaxInformation?: string;
    CorporateEntityName?: string;
    BusinessRegistrationNumber?: string;
    AuthorizedRepresentative?: string;
    CorporateTaxInformation?: string;
    GovernmentAgencyName?: string;
    PartnerNames?: string;
    RegisteredPartnershipNumber?: string;
    PartnershipTaxInformation?: string;
    TrusteeName?: string;
    BeneficiariesInformation?: string;
    EstateTaxInformation?: string;
    ApplicantType?: string;
    PropertyUseType?: string;
    PropertyType?: string;
  };
  handleSubmit: (val: Record<string, unknown>) => void;
}

const ApplicationDetailsForm = forwardRef<
  HTMLFormElement,
  ApplicationDetailsFormProps
>(({ formData, handleSubmit }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permit } = useParams();
  const [applicantFields, setApplicantFields] = useState([]);
  const [propertyUseFields, setPropertyUseFields] = useState([]);
  const [propertyTypeFields, setPropertyTypeFields] = useState([]);

  // const handleChange = (val: string | number, e: { name: string }) => {
  //   const { name } = e;
  //   dispatch(updateFormData({ [name]: val }));
  // };

  const OwnersName = useSelector(
    (state: RootState) => state.constructionFormSlice.formData?.OwnersName
  );
  
  const handleChange = (val: string | number, e: { name: string }) => {
    const { name } = e;
    
    // Create the update object
    const updateData: { [key: string]: any } = { [name]: val };
    
    // If PropertyType is being changed, apply conditional logic for OwnersName
    if (name === "PropertyType") {
      if (val !== "Private") {
        // If PropertyType is "Private", add OwnersName by default
        updateData.OwnersName = OwnersName || ""; // Keep existing value or set empty string
      }
    }
    
    // Dispatch the update
    dispatch(updateFormData(updateData));
  };

  const getFieldsByApplicantType = (applicantType: string): Fields[] => {
    const fieldConfigs = {
      Owner: [
        {
          fieldName: 'OwnersName',
          initialValue: formData?.OwnersName || '',
          fieldLabel: 'Owner Name',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'OwnerMobileNumber',
          initialValue: formData?.OwnerMobileNumber || '',
          fieldLabel: 'Owner Mobile Number',
          fieldType: 'mobile',
          required: true,
          dependency: handleChange
        }
      ],
      Agent: [
        {
          fieldName: 'AgentsLicenseNumber',
          initialValue: formData?.AgentsLicenseNumber || '',
          fieldLabel: "Agent's License Number",
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'CompanyName',
          initialValue: formData?.CompanyName || '',
          fieldLabel: 'Company Name',
          fieldType: 'text',
          required: false,
          dependency: handleChange
        }
      ],
      Contractor: [
        {
          fieldName: 'ContractorLicenseNumber',
          initialValue: formData?.ContractorLicenseNumber || '',
          fieldLabel: 'Contractor License Number',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'CompanyName',
          initialValue: formData?.CompanyName || '',
          fieldLabel: 'Company Name',
          fieldType: 'text',
          required: false,
          dependency: handleChange
        }
      ],
      Architect: [
        {
          fieldName: 'ArchitectLicenseNumber',
          initialValue: formData?.ArchitectLicenseNumber || '',
          fieldLabel: 'Architect License Number',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        }
      ],
      'Tenant/Lease': [
        {
          fieldName: 'TenantName',
          initialValue: formData?.TenantName || '',
          fieldLabel: "Tenant's Name",
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'TenantEmail',
          initialValue: formData?.TenantEmail || '',
          fieldLabel: "Tenant's Email",
          fieldType: 'email',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'TenantPhone',
          initialValue: formData?.TenantPhone || '',
          fieldLabel: "Tenant's Phone Number",
          fieldType: 'mobile',
          required: true,
          dependency: handleChange
        }
      ]
    };

    return fieldConfigs[applicantType] || [];
  };

  const getFieldsByPropertyUse = (propertyType: string): Fields[] => {
    const fieldConfigs = {
      Residential: [
        {
          fieldName: 'NumberOfUnits',
          initialValue: formData?.NumberOfUnits || '',
          fieldLabel: 'Number of Units',
          fieldType: 'number',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'NumberOfBedrooms',
          initialValue: formData?.NumberOfBedrooms || '',
          fieldLabel: 'Number of Bedrooms',
          fieldType: 'number',
          required: true,
          dependency: handleChange
        }
      ],
      Commercial: [
        {
          fieldName: 'BusinessOccupancyClassification',
          initialValue: formData?.BusinessOccupancyClassification || '',
          fieldLabel: 'Business Occupancy Classification',
          fieldType: 'note',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'ParkingRequirements',
          initialValue: formData?.ParkingRequirements || '',
          fieldLabel: 'Parking Requirements',
          fieldType: 'note',
          required: true,
          dependency: handleChange
        }
      ],
      Industrial: [
        {
          fieldName: 'IndustrialEquipmentSpecifications',
          initialValue: formData?.IndustrialEquipmentSpecifications || '',
          fieldLabel: 'Industrial Equipment Specifications',
          fieldType: 'note',
          required: true,
          dependency: handleChange
        }
      ],
      Agriculture: [
        {
          fieldName: 'AgriculturalEquipmentSpecifications',
          initialValue: formData?.AgriculturalEquipmentSpecifications || '',
          fieldLabel: 'Agricultural Equipment Specifications',
          fieldType: 'note',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'LandUseAndCropPlan',
          initialValue: formData?.LandUseAndCropPlan || '',
          fieldLabel: 'Land Use and Crop Plan',
          fieldType: 'note',
          required: true,
          dependency: handleChange
        }
      ],
      'Mixed-use': [
        {
          fieldName: 'ResidentialUnitBreakdown',
          initialValue: formData?.ResidentialUnitBreakdown || '',
          fieldLabel: 'Residential Unit Breakdown',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'CommercialUnitBreakdown',
          initialValue: formData?.CommercialUnitBreakdown || '',
          fieldLabel: 'Commercial Unit Breakdown',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'SharedFacilityManagementPlan',
          initialValue: formData?.SharedFacilityManagementPlan || '',
          fieldLabel: 'Shared Facility Management Plan',
          fieldType: 'note',
          required: true,
          dependency: handleChange
        }
      ]
    };

    return fieldConfigs[propertyType] || [];
  };

  const getFieldsByPropertyType = (propertyType: string): Fields[] => {
    const fieldConfigs = {
      Private: [
        {
          fieldName: 'PrivatePropertyUsePlan',
          initialValue: formData?.PrivatePropertyUsePlan || '',
          fieldLabel: 'Private Property Use Plan',
          fieldType: 'note',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'PropertyTaxInformation',
          initialValue: formData?.PropertyTaxInformation || '',
          fieldLabel: 'Property Tax Information',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        }
      ],
      Corporate: [
        {
          fieldName: 'CorporateEntityName',
          initialValue: formData?.CorporateEntityName || '',
          fieldLabel: 'Corporate Entity Name',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'BusinessRegistrationNumber',
          initialValue: formData?.BusinessRegistrationNumber || '',
          fieldLabel: 'Business Registration Number',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'AuthorizedRepresentative',
          initialValue: formData?.AuthorizedRepresentative || '',
          fieldLabel: 'Authorized Representative',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'CorporateTaxInformation',
          initialValue: formData?.CorporateTaxInformation || '',
          fieldLabel: 'Corporate Tax Information',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        }
      ],
      Public: [
        {
          fieldName: 'GovernmentAgencyName',
          initialValue: formData?.GovernmentAgencyName || '',
          fieldLabel: 'Government Agency Name',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        }
      ],
      Partnership: [
        {
          fieldName: 'PartnerNames',
          initialValue: formData?.PartnerNames || '',
          fieldLabel: 'Partner Name',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'RegisteredPartnershipNumber',
          initialValue: formData?.RegisteredPartnershipNumber || '',
          fieldLabel: 'Registered Partnership Number',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'PartnershipTaxInformation',
          initialValue: formData?.PartnershipTaxInformation || '',
          fieldLabel: 'Partnership Tax Information',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        }
      ],
      'Trust or estate': [
        {
          fieldName: 'TrusteeName',
          initialValue: formData?.TrusteeName || '',
          fieldLabel: 'Trustee Name',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },

        {
          fieldName: 'BeneficiariesInformation',
          initialValue: formData?.BeneficiariesInformation || '',
          fieldLabel: 'Beneficiaries Information',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        },
        {
          fieldName: 'EstateTaxInformation',
          initialValue: formData?.EstateTaxInformation || '',
          fieldLabel: 'Estate Tax Information',
          fieldType: 'text',
          required: true,
          dependency: handleChange
        }
      ]
    };

    if (formData?.ApplicantType !== 'Owner') {
      fieldConfigs.Private.push({
        fieldName: 'OwnersName',
        initialValue: formData?.OwnersName || '',
        fieldLabel: "Owner's Name",
        fieldType: 'text',
        required: true,
        dependency: handleChange
      });
    }

    return fieldConfigs[propertyType] || [];
  };

  useEffect(() => {
    if (formData?.ApplicantType) {
      setApplicantFields(getFieldsByApplicantType(formData?.ApplicantType));
    }
    if (formData?.PropertyUseType) {
      setPropertyUseFields(getFieldsByPropertyUse(formData?.PropertyUseType));
    }
    if (formData?.PropertyType) {
      setPropertyTypeFields(getFieldsByPropertyType(formData?.PropertyType));
    }
  }, []);

  return (
    <FormikForm
      innerRef={ref}
      allFields={[
        ...applicantFields,
        ...propertyUseFields,
        ...propertyTypeFields
      ]}
      buttonTitle={['Cancel']}
      heading={'Application & Applicant Details'}
      buttonPlacement={'right'}
      formMethod={handleSubmit}
      handleOtherButton={() => {
        navigate(`/dashboard/${permit}`);
      }}
    />
  );
});

ApplicationDetailsForm.displayName = 'ApplicationDetailsForm';

export default ApplicationDetailsForm;
